/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import type { Maybe } from 'graphql/jsutils/Maybe';

import { useRetracked } from 'js/lib/retracked';
import useRouter, { useLocation } from 'js/lib/useRouter';

import {
  Button as CDSButton,
  Grid,
  Pagination,
  Search,
  SelectField,
  SelectOption,
  Typography2,
  breakpoints,
  useTheme,
} from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type {
  EntWebsiteBlockResourceHubCollectionWithSearchQuery,
  EntWebsiteResourceCardFragment,
  EntWebsiteResourceHubCollectionWithSearchFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { EntWebsiteContext } from 'bundles/ent-website/components/EntWebsiteContext';
import ResourceHubCard from 'bundles/ent-website/components/ResourceHubCard';
import type { DOMAINS } from 'bundles/ent-website/constants/navigation';
import { DOMAIN_NAME_MAPPINGS } from 'bundles/ent-website/constants/navigation';
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import { convertDomains } from 'bundles/ent-website/contentful/utils';
import { useDebouncedValue } from 'bundles/ent-website/hooks/useDebouncedValue';
import { useResourceHubSearchQuery } from 'bundles/ent-website/hooks/useResourceHubSearchQuery';
import { entWebsiteBlockResourceHubCollectionWithSearchQuery } from 'bundles/ent-website/queries/EntWebsiteResourceHubCollectionWithSearch';
import type { ResourceHubCardItem } from 'bundles/ent-website/types';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { genResourceHubPages } from 'bundles/ent-website/utils/resourceHubUtils';
import { buildURL, equalParams } from 'bundles/ent-website/utils/urlUtils';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(CDSButton);

const styles = {
  container: css`
    scroll-margin-top: 150px;
    ${breakpoints.down('sm')} {
      scroll-margin-top: 85px;
    }
  `,
  multiSelectCnt: css`
    button {
      width: 100%;
      max-width: unset;
    }
  `,
  headlineWrapper: css`
    display: flex;
    margin-bottom: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      flex-direction: column;
    }

    h2 {
      margin-bottom: 0;
    }

    a {
      margin-left: var(--cds-spacing-300);
      ${breakpoints.down('xs')} {
        margin-left: 0;
        margin-top: 12px;

        span {
          display: flex;
          justify-content: start;
        }
      }
    }
  `,
  resourcesWrapper: css`
    gap: var(--cds-spacing-400);
    row-gap: var(--cds-spacing-800);
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    ${breakpoints.down('md')} {
      grid-template-columns: repeat(3, 1fr);
    }

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${breakpoints.down('xs')} {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
  useCaseSelectCnt: css`
    ${breakpoints.down('sm')} {
      padding: 12px 0;
    }
  `,
  selectField: css`
    > div {
      margin: 0 !important;
    }
  `,
};

const placeholderShimmer = keyframes`
  0% {
    background-position: -500px 0;
  }
  100% {
    background-position: 500px 0;
  }
`;

const placeholderStyles = {
  cardContainer: css`
    min-height: 335px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: 8px;
    overflow: hidden;
  `,
  cardContent: css`
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    row-gap: 16px;
  `,
  cardHeader: css`
    display: grid;
    row-gap: 8px;
    position: relative;
    aspect-ratio: 16/9;
  `,
  headlineCnt: css`
    flex: 1;
  `,
  headileCopy: (_theme: Theme, width = '100%') => css`
    height: 16px;
    width: ${width};
    margin-bottom: 8px;
  `,
  button: css`
    height: 32px;
    width: 60%;
  `,
  bodyContainer: css`
    display: flex;
    height: 100%;
    flex-direction: column;
    flex: 1;
  `,
  animated: css`
    background-color: #f3f3f3;
    background: linear-gradient(to right, #f3f3f3 5%, #eee 20%, #f3f3f3 35%);
    animation: ${placeholderShimmer} 2s linear 0s infinite normal forwards;
  `,
};

const DEFAULT_ITEMS_PER_PAGE = 12;
// '' is required for the default type of domain for the select box
// to work correctly and display the placeholder.
const ALL_DOMAINS_ARRAY = ['all', ''];

export const ResourceHubCardPlaceholder = () => {
  const theme = useTheme();
  return (
    <div css={placeholderStyles.cardContainer} data-testid="card-placeholder">
      <div css={placeholderStyles.cardContent}>
        <div css={[placeholderStyles.cardHeader, placeholderStyles.animated]} />
        <div css={placeholderStyles.bodyContainer}>
          <div css={placeholderStyles.headlineCnt}>
            <p css={[placeholderStyles.headileCopy(theme, '90%'), placeholderStyles.animated]} />
            <p css={[placeholderStyles.headileCopy(theme, '100%'), placeholderStyles.animated]} />
            <p css={[placeholderStyles.headileCopy(theme, '80%'), placeholderStyles.animated]} />
          </div>
          <div>
            <p css={[placeholderStyles.button, placeholderStyles.animated]} />
          </div>
        </div>
      </div>
    </div>
  );
};

const placeholderGridStyles = {
  gridRow: css`
    gap: var(--cds-spacing-400);
    row-gap: var(--cds-spacing-800);
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    margin-top: 24px;

    ${breakpoints.down('md')} {
      grid-template-columns: repeat(3, 1fr);
    }

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${breakpoints.down('xs')} {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
};

export const PlaceholderGrid = () => {
  return (
    <React.Fragment>
      <div css={placeholderGridStyles.gridRow}>
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
        <ResourceHubCardPlaceholder />
      </div>
    </React.Fragment>
  );
};

export type Props = BlockModuleInnerProps<EntWebsiteResourceHubCollectionWithSearchFragment> & {
  itemsPerPage?: number;
};

export type ResourceHubSearchResultsProps = {
  module: EntWebsiteResourceHubCollectionWithSearchFragment;
  searchString: string;
  useCase: string;
  domain: string;
  domains: string[];
  contentTypes?: Maybe<Maybe<string>[]>;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  resetFilters: () => void;
  scrollToSearchBar: () => void;
  itemsPerPage?: number;
  listOfCardSlugsToExcludeFromSearch?: Maybe<Maybe<string>[]>;
};

export const DFAULT_USE_CASE_VALUE = 'All';

export const ResourceHubSearchResults = ({
  module,
  searchString,
  useCase,
  domain,
  domains,
  contentTypes,
  resetFilters,
  pageNumber,
  setPageNumber,
  scrollToSearchBar,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  listOfCardSlugsToExcludeFromSearch,
}: ResourceHubSearchResultsProps) => {
  const track = useRetracked();

  const enablePagination = true;
  const { isPreview } = useContext(EntWebsiteContext);
  const typedDomains = convertDomains(domains);

  const { data: mergedData, loading: mergedLoading } = useResourceHubSearchQuery({
    searchString,
    useCase,
    domains: ALL_DOMAINS_ARRAY.includes(domain) ? [...typedDomains] : [domain],
    contentTypes,
    listOfCardSlugsToExcludeFromSearch,
    isPreview,
  });

  const pages: Array<Maybe<{ __typename?: 'EntWebsiteResourceCard' } & EntWebsiteResourceCardFragment>>[] = useMemo(
    () => genResourceHubPages(mergedData ?? [], itemsPerPage),
    [mergedData, itemsPerPage]
  );

  const cardsToShow = useMemo(
    () => (enablePagination ? pages[pageNumber - 1] : mergedData),
    [mergedData, enablePagination, pageNumber, pages]
  );

  const onPageChange = useCallback(
    (_e, idx) => {
      track({ trackingName: 'page_change', action: 'click', trackingData: { pageNumber: idx } });
      setPageNumber(idx);
      scrollToSearchBar();
    },
    [scrollToSearchBar, setPageNumber, track]
  );

  if (mergedLoading) return <PlaceholderGrid />;

  return (
    <Grid
      css={css`
        margin-top: 48px;
      `}
    >
      {cardsToShow && cardsToShow?.length > 0 ? (
        <div css={styles.resourcesWrapper}>
          {cardsToShow?.map(
            (item: Maybe<ResourceHubCardItem>) => item && <ResourceHubCard key={item?.slug} item={item} />
          )}
        </div>
      ) : (
        <Grid container alignItems="center" justifyContent="center">
          <Grid item sm={12} md={4}>
            <Typography2
              component="h3"
              variant="subtitleMedium"
              align="center"
              css={css`
                font-size: 2rem;
              `}
            >
              {_t('No results found')}
            </Typography2>
            <TrackedButton
              onClick={resetFilters}
              trackingName={eventingTransform(`${module.__typename}_${module.slug}_button_reset_filters`)}
              fullWidth
              variant="ghost"
              css={css`
                margin-top: 24px;
                background: none !important;
              `}
            >
              {_t('Reset filters')}
            </TrackedButton>
          </Grid>
        </Grid>
      )}
      {(mergedData?.length ?? 0) > itemsPerPage && enablePagination === true ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          css={css`
            margin-top: 48px;
          `}
        >
          <Pagination
            total={mergedData?.length ?? 0}
            pageSize={itemsPerPage}
            page={pageNumber}
            hidePageSizeInput
            onPageChange={onPageChange}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export function ResourceHubV2({ content, assembly, itemsPerPage = DEFAULT_ITEMS_PER_PAGE }: Props) {
  const location = useLocation();
  const router = useRouter();
  const track = useRetracked();

  const { useCaseFilters, enableSearch, domains } = content;
  // When User Opens a Page With URL params - load them into state
  const [searchString, setSearchString] = useState<string>(location?.query?.searchString ?? '');
  const [useCase, setUseCase] = useState<string>(location?.query?.useCase ?? '');
  const [domain, setDomain] = useState<string>(location?.query?.domain ?? '');
  const [pageNumber, setPageNumber] = useState<number>(Number(location?.query?.pageNumber ?? 1));
  const typedDomains = convertDomains(domains);

  const { debouncedValue: debouncedSearchString, forceUpdate: forceUpdateDebouncedSearchString } =
    useDebouncedValue(searchString);

  const updateURLParam = useCallback(
    (key: string, val?: string, options: { deletePageNumber: boolean } = { deletePageNumber: true }) => {
      const searchParams = new URLSearchParams(window?.location?.search);

      if (equalParams(searchParams?.get(key), val)) return;

      if (val && val?.length > 0) {
        searchParams.set(key, val);
      } else {
        searchParams.delete(key);
      }

      if (options?.deletePageNumber) searchParams.delete('pageNumber');

      router.push(buildURL(location?.pathname, searchParams));
    },
    [location?.pathname, router]
  );

  const resetFilters = useCallback(() => {
    const searchParams = new URLSearchParams(window?.location?.search);
    setSearchString('');
    forceUpdateDebouncedSearchString('');
    setUseCase('');
    setDomain('');

    searchParams.delete('searchString');
    searchParams.delete('useCase');
    searchParams.delete('pageNumber');

    router.push(buildURL(location?.pathname, searchParams));
  }, [forceUpdateDebouncedSearchString, location?.pathname, router]);

  const onClearSearch = useCallback(() => {
    setSearchString('');
    forceUpdateDebouncedSearchString('');
  }, [forceUpdateDebouncedSearchString]);

  // Scrolling to top of the pagination results once page change triggered by user
  const scrollToSearchBar = useCallback(() => {
    setTimeout(() => {
      const el = document.getElementsByClassName('rc-ResourceHubCollectionWithSearch')?.[0];
      el?.scrollIntoView?.();
    }, 10);
  }, []);

  // When State Updates - Update URL
  useEffect(() => {
    updateURLParam('searchString', debouncedSearchString);
  }, [debouncedSearchString, track, updateURLParam]);

  // Not taking updateURLParam dep
  useEffect(() => {
    track({ trackingName: 'search_string', action: 'change', trackingData: { value: debouncedSearchString } });
  }, [debouncedSearchString, track]);

  useEffect(() => {
    updateURLParam('useCase', useCase);
  }, [useCase, updateURLParam]);

  useEffect(() => {
    updateURLParam('domain', domain);
  }, [domain, updateURLParam]);

  useEffect(() => {
    updateURLParam('pageNumber', pageNumber > 1 ? String(pageNumber) : undefined, { deletePageNumber: false });
  }, [updateURLParam, pageNumber]);

  // When user hits back and forward - load update URL params into state
  // Since the state updates triggered only when there is a change in state - there should be no endless loops
  useEffect(() => {
    const listener = () => {
      const sp = new URLSearchParams(window.location?.search);

      setUseCase(sp.get('useCase') ?? '');
      setDomain(sp.get('domain') ?? '');
      setSearchString(sp.get('searchString') ?? '');
      setPageNumber(Number(sp.get('pageNumber') ?? 1));
      forceUpdateDebouncedSearchString(sp.get('searchString') ?? '');
      scrollToSearchBar();
    };

    // Supporting browser native navigation
    window.addEventListener('popstate', listener);
    return () => {
      window.removeEventListener('popstate', listener);
    };
  }, [forceUpdateDebouncedSearchString, scrollToSearchBar, useCase, domain]);

  // Make sure that pageNumber set to 1 if there is not pageNumber in query
  useEffect(() => {
    if (!location.query.pageNumber) setPageNumber(1);
  }, [location, setPageNumber]);

  const onUseCaseChange = useCallback(
    (e) => {
      track({ trackingName: 'use_case_filters', action: 'change', trackingData: { value: e?.target?.value } });
      setUseCase(e.target.value as string);
    },
    [track]
  );

  const onDomainChange = useCallback(
    (e) => {
      track({ trackingName: 'domain_filters', action: 'change', trackingData: { value: e?.target?.value } });
      setDomain(e.target.value as string);
    },
    [track]
  );

  const contentPanel = useCallback(() => {
    return (
      <Grid container direction="column" className="rc-ResourceHubCollectionWithSearch" css={styles.container}>
        <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={8}>
          <Grid item md={3} sm={12} xs={12} css={[styles.multiSelectCnt, styles.useCaseSelectCnt]}>
            {useCaseFilters?.filtersCollection?.items !== undefined &&
              useCaseFilters?.filtersCollection?.items?.length > 0 && (
                <SelectField
                  fullWidth
                  renderLabel={() => <></>}
                  css={styles.selectField}
                  label={_t('Topic')}
                  value={useCase}
                  placeholder={_t('Topic')}
                  onChange={onUseCaseChange}
                >
                  {[
                    { label: _t('All'), value: DFAULT_USE_CASE_VALUE },
                    ...useCaseFilters?.filtersCollection?.items,
                  ]?.map((filterEntry) => (
                    <SelectOption key={filterEntry?.value} value={filterEntry?.value ?? ''}>
                      {filterEntry?.label ?? ''}
                    </SelectOption>
                  ))}
                </SelectField>
              )}
          </Grid>

          {typedDomains.length > 1 && (
            <Grid item md={3} sm={12} xs={12} css={[styles.multiSelectCnt, styles.useCaseSelectCnt]}>
              <SelectField
                fullWidth
                renderLabel={() => <></>}
                css={styles.selectField}
                label={_t('Industry Focus')}
                value={domain}
                placeholder={_t('Industry Focus')}
                onChange={onDomainChange}
              >
                {['all', ...typedDomains].map((domainEntry) => (
                  <SelectOption key={domainEntry} value={domainEntry ?? ''}>
                    {/* Unsure of a good way to do this default value */}
                    {ALL_DOMAINS_ARRAY.includes(domainEntry ?? '')
                      ? _t('All')
                      : DOMAIN_NAME_MAPPINGS[domainEntry as keyof typeof DOMAINS]}
                  </SelectOption>
                ))}
              </SelectField>
            </Grid>
          )}

          <Grid item md={6} sm={12} xs={12}>
            {enableSearch && (
              <Search
                fullWidth
                value={searchString}
                onChange={setSearchString}
                onSearch={forceUpdateDebouncedSearchString}
                onClear={onClearSearch}
              />
            )}
          </Grid>
        </Grid>

        <ResourceHubSearchResults
          module={content}
          contentTypes={content?.contentTypes}
          searchString={debouncedSearchString}
          useCase={useCase}
          domain={domain}
          domains={typedDomains}
          resetFilters={resetFilters}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          scrollToSearchBar={scrollToSearchBar}
          itemsPerPage={itemsPerPage}
          listOfCardSlugsToExcludeFromSearch={content?.listOfCardSlugsToExcludeFromSearch}
        />
      </Grid>
    );
  }, [
    useCaseFilters?.filtersCollection?.items,
    useCase,
    domain,
    domains,
    onDomainChange,
    typedDomains,
    onUseCaseChange,
    enableSearch,
    searchString,
    forceUpdateDebouncedSearchString,
    onClearSearch,
    content,
    debouncedSearchString,
    resetFilters,
    pageNumber,
    scrollToSearchBar,
    itemsPerPage,
  ]);

  const imagePanel = () => null;

  return (
    <BlockLayout
      assembly={assembly}
      renderContent={contentPanel}
      renderImage={imagePanel}
      blockStyle={css`
        padding-top: var(--cds-spacing-600);
      `}
    />
  );
}

export const BlockResourceHubV2 = withBlockQuery<
  EntWebsiteResourceHubCollectionWithSearchFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockResourceHubCollectionWithSearchQuery
>(entWebsiteBlockResourceHubCollectionWithSearchQuery)(ResourceHubV2);
